import {ElementRef, Injectable, Injector} from '@angular/core'
import {ComponentPortal, PortalInjector} from '@angular/cdk/portal'
import {ConnectionPositionPair, Overlay, OverlayConfig, PositionStrategy} from '@angular/cdk/overlay'
import {NavigationEnd, Router} from '@angular/router'
import {PopoverContent, PopoverReference} from 'app/shared/popover/popover-ref'
import {LayoutService} from './layout.service'
import {PopoverComponent} from 'app/shared/popover/popover.component'
import {select, Store} from '@ngrx/store'
import {AppState} from 'app/core/core.state'
import {selectGlobalReference, selectTotalScreenSize} from 'app/layout/layout.selector';
import {LayoutActions} from 'app/layout'
import {ScreenSize} from 'app/models/common/screenSize'

export enum PopoverType {
	tooltip,
	modal,
	drawer,
	dock,
	configurer
}

export interface PopoverConfig {
	title?: string;
	type?: PopoverType;
	className?: string;
	width?: string;
	height?: string;
	origin?: any;
	flexible?: boolean;
	push?: boolean;
	positions?: ConnectionPositionPair[],
	content?: PopoverContent;
	data?: any;
	hasCloseButton?: boolean;
	hasBackdrop?: boolean;
	backdropClass?: string;
	closeExisting?: boolean;
}

@Injectable({
	providedIn: 'root'
})
export class PopoverService {
	private currentPopoverReferences: PopoverReference[] = [];
	private dockingStrategyOffsetElement: ElementRef;
	private currentScreenType: ScreenSize;

	constructor(
		private store: Store<AppState>,
		private overlay: Overlay,
		private injector: Injector,
		private layoutService: LayoutService,
		private router: Router
	) {
		this.store.pipe(select(selectTotalScreenSize)).subscribe(type => {
			this.currentScreenType = type;
		})
	}

	open({
		     title,
		     type,
		     className,
		     width,
		     height,
		     origin,
		     flexible,
		     push,
		     positions,
		     content,
		     data,
		     hasCloseButton,
		     closeExisting
	     }: PopoverConfig): PopoverReference {
		this.store.pipe(select(selectGlobalReference)).subscribe(el => {
			this.dockingStrategyOffsetElement = el;
		});
		if (closeExisting) {
			this.closeAll();
		}
		if (hasCloseButton == null) {
			hasCloseButton = true;
		}
		let customOrigin = true;
		if (type == null) {
			type = this.getOverlayType();
		}
		if (!origin) {
			customOrigin = false;
			origin = this.dockingStrategyOffsetElement.nativeElement;
		}
		if (!width && origin.offsetWidth) {
			width = origin.offsetWidth;
		}
		if (type === PopoverType.dock && !height) {
			height = '100%';
		}
		if (type === PopoverType.dock) {
			this.store.dispatch(LayoutActions.setPopoverDocked({popoverDocked: true}));
			this.layoutService.setPopoverWidth(width);
		}

		const config = this.getOverlayConfig({type, origin, width, height, flexible, push, positions});
		const overlayRef = this.overlay.create(config);
		const popoverReference = new PopoverReference(overlayRef, title, hasCloseButton, className, content, data);
		const injector = this.createInjector(popoverReference, this.injector);
		if (config.hasBackdrop && hasCloseButton) {
			overlayRef.backdropClick().subscribe(_ => popoverReference.close({goBack: false}));
		}
		overlayRef.attach(new ComponentPortal(PopoverComponent, null, injector));
		this.currentPopoverReferences.push(popoverReference);
		this.router.events.subscribe((routeEvent) => {
			if (routeEvent instanceof NavigationEnd) {
				this.closeAll();
			}
		});

		if (type === PopoverType.dock) {
			overlayRef.detachments().subscribe(value => {
				this.store.dispatch(LayoutActions.setPopoverDocked({popoverDocked: false}));
			});
		}

		return popoverReference;
	}

	openModal({title, content, data, hasCloseButton}: PopoverConfig) {
		return this.open({
			title,
			type: PopoverType.modal,
			className: 'mat-elevation-z4',
			content,
			data,
			hasCloseButton,
			closeExisting: true
		})
	}

	openTooltip({title, content, data, origin, positions}: PopoverConfig) {
		return this.open({
			title,
			type: PopoverType.tooltip,
			className: 'mat-elevation-z2',
			content,
			data,
			origin,
			positions
		})
	}

	openDrawer({title, content, data, hasCloseButton, width}: PopoverConfig) {
		return this.open({
			title,
			type: PopoverType.drawer,
			className: 'mat-elevation-z4',
			content,
			data,
			hasCloseButton: hasCloseButton,
			hasBackdrop: true,
			width,
			closeExisting: true,
			backdropClass: 'dark-backdrop'
		})
	}

	openConfigurer({title, content, data, origin, positions}: PopoverConfig) {
		return this.open({
			title,
			type: PopoverType.configurer,
			className: 'mat-elevation-z2',
			content,
			data,
			origin,
			positions
		})
	}

	closeAll(goBack: boolean = false) {
		if (this.currentPopoverReferences) {
			let currentPopoverReference: PopoverReference;
			while (currentPopoverReference = this.currentPopoverReferences.pop()) {
				currentPopoverReference.close({goBack: goBack});
			}
		}
	}

	private getOverlayConfig({type, origin, width, height, flexible, push, positions}): OverlayConfig {


		return new OverlayConfig({
			hasBackdrop: this.checkIfHasBackDrop(type),
			width: width,
			height: height,
			disposeOnNavigation: true,
			backdropClass: 'dark-backdrop',
			positionStrategy: this.getOverlayPosition(type, origin, flexible, push, positions),
			scrollStrategy: this.overlay.scrollStrategies.reposition()
		})
	}

	private checkIfHasBackDrop(type: PopoverType) {
		if (type === PopoverType.modal || type === PopoverType.drawer || type === PopoverType.configurer) {
			return true
		} else {
			return false
		}
	}

	private getOverlayType() {
		if (this.currentScreenType.xs) {
			return PopoverType.modal;
		} else if (this.currentScreenType.md) {
			return PopoverType.drawer;
		} else if (this.currentScreenType.lg) {
			return PopoverType.dock;
		} else {
			throw new Error('Unknown screen type')
		}
	}

	private getOverlayPosition(type: PopoverType, origin: HTMLElement, flexible: boolean, push: boolean, positions: ConnectionPositionPair[]): PositionStrategy {
		if (type === PopoverType.modal) {
			return this.overlay
				.position()
				.global()
				.centerHorizontally()
				.centerVertically()
		} else if (type === PopoverType.drawer) {
			return this.overlay
				.position()
				.global()
				.right()
		} else if (type === PopoverType.dock || type === PopoverType.tooltip || type === PopoverType.configurer) {
			return this.overlay
				.position()
				.flexibleConnectedTo(origin)
				.withPositions(positions ? positions : this.getPositions())
				.withFlexibleDimensions(flexible ? flexible : false)
				.withPush(push ? push : false)
		} else {
			throw new Error('Unknown type')
		}
	}

	private createInjector(popoverReference: PopoverReference, injector: Injector) {
		const tokens = new WeakMap([[PopoverReference, popoverReference]])
		return new PortalInjector(injector, tokens)
	}

	private getPositions(): ConnectionPositionPair[] {
		return [
			{
				originX: 'start',
				originY: 'top',
				overlayX: 'start',
				overlayY: 'top'
			}
		]
	}
}
